/**
 * Organization Service
 *
 * Service for Ailyce admins to manage organizations, users, and impersonation.
 */

import api from './api';

const organizationService = {
  /**
   * Get organization statistics (admin only)
   */
  getOrganizationStats: async () => {
    try {
      const response = await api.get('/admin/organizations/stats');
      return response.data;
    } catch (error) {
      console.error('Error getting organization stats:', error);
      throw error;
    }
  },

  /**
   * List all organizations (admin only)
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (active, suspended, inactive)
   * @param {number} params.limit - Max results (default: 100)
   * @param {number} params.offset - Pagination offset (default: 0)
   */
  listOrganizations: async (params = {}) => {
    try {
      const response = await api.get('/admin/organizations', { params });
      return response.data;
    } catch (error) {
      console.error('Error listing organizations:', error);
      throw error;
    }
  },

  /**
   * Get detailed information about an organization (admin only)
   * @param {string} organizationId - Organization UUID
   */
  getOrganizationDetails: async (organizationId) => {
    try {
      const response = await api.get(`/admin/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting organization details:', error);
      throw error;
    }
  },

  /**
   * Create a new organization (admin only)
   * @param {Object} data - Organization data
   * @param {string} data.name - Organization name
   * @param {string} data.slug - Organization slug (subdomain)
   * @param {string} data.admin_email - Admin user email
   * @param {string} data.admin_name - Admin user full name
   * @param {string} data.admin_password - Admin user password
   * @param {boolean} data.provision_dns - Whether to provision DNS (default: false)
   * @param {string} data.subscription_plan - Subscription plan (default: 'free')
   */
  createOrganization: async (data) => {
    try {
      const response = await api.post('/admin/organizations', data);
      return response.data;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  /**
   * Update an organization (admin only)
   * @param {string} organizationId - Organization UUID
   * @param {Object} data - Update data
   * @param {string} data.name - Organization name (optional)
   * @param {string} data.status - Organization status (active, suspended, inactive) (optional)
   * @param {string} data.subscription_plan - Subscription plan (optional)
   */
  updateOrganization: async (organizationId, data) => {
    try {
      const response = await api.patch(`/admin/organizations/${organizationId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  },

  /**
   * Delete an organization (soft delete - sets status to inactive) (admin only)
   * @param {string} organizationId - Organization UUID
   */
  deleteOrganization: async (organizationId) => {
    try {
      const response = await api.delete(`/admin/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  },

  /**
   * Start impersonating a user in an organization (admin only)
   * @param {string} organizationId - Organization UUID
   * @param {Object} data - Impersonation data
   * @param {string} data.target_user_id - Target user UUID
   * @param {string} data.reason - Reason for impersonation (min 10 characters)
   * @param {number} data.duration_minutes - Session duration in minutes (15-480, default: 60)
   */
  startImpersonation: async (organizationId, data) => {
    try {
      const response = await api.post(`/admin/organizations/${organizationId}/impersonate`, data);
      return response.data;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  },

  /**
   * Get impersonation sessions for an organization (admin only)
   * @param {string} organizationId - Organization UUID
   * @param {boolean} activeOnly - Only get active sessions (default: false)
   */
  getImpersonationSessions: async (organizationId, activeOnly = false) => {
    try {
      const response = await api.get(
        `/admin/organizations/${organizationId}/impersonation-sessions`,
        { params: { active_only: activeOnly } }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting impersonation sessions:', error);
      throw error;
    }
  },

  /**
   * End an impersonation session (admin only)
   * @param {string} sessionId - Session UUID
   */
  endImpersonation: async (sessionId) => {
    try {
      const response = await api.post(`/admin/impersonation/${sessionId}/end`);
      return response.data;
    } catch (error) {
      console.error('Error ending impersonation:', error);
      throw error;
    }
  }
};

export default organizationService;
